import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getAiChatTokenRequest } from '../../sagas/aiChatToken/actions';

const AI_CHAT_TOKEN_REFRESH_BUFFER_MS = 30 * 1000;

export const isAiChatTokenValid = expiresAt => {
  if (!expiresAt) return false;

  return new Date(expiresAt).getTime() - Date.now() > AI_CHAT_TOKEN_REFRESH_BUFFER_MS;
};

// Keeps the AI chat token fresh while a chat is active and remounts the chat (via chatKey) after an auth-expired reconnect.
const useAiChatSession = ({ active }) => {
  const dispatch = useDispatch();
  const {
    token: aiChatToken,
    expiresAt: aiChatTokenExpiresAt,
    loading: aiChatTokenLoading
  } = useSelector(state => state.aiChatToken || {});

  const [chatKey, setChatKey] = useState(0);
  const refreshingRef = useRef(false);
  const pendingAuthReconnectRef = useRef(false);
  const prevTokenRef = useRef(aiChatToken);
  const wasBackgroundedRef = useRef(false);

  // Remount chat only when we explicitly requested auth-expired reconnect.
  useEffect(() => {
    if (refreshingRef.current && aiChatToken && aiChatToken !== prevTokenRef.current) {
      refreshingRef.current = false;
      prevTokenRef.current = aiChatToken;
      if (pendingAuthReconnectRef.current) {
        pendingAuthReconnectRef.current = false;
        setChatKey(k => k + 1);
      }
    }
  }, [aiChatToken]);

  useEffect(() => {
    if (!refreshingRef.current || aiChatTokenLoading) {
      return;
    }

    // Request finished but token did not rotate (for example refresh failed).
    refreshingRef.current = false;
    pendingAuthReconnectRef.current = false;
  }, [aiChatTokenLoading]);

  const refreshChatSession = useCallback(({ forceTokenRefresh = false } = {}) => {
    if (!active || refreshingRef.current) {
      return;
    }

    const shouldRefreshToken = forceTokenRefresh || !isAiChatTokenValid(aiChatTokenExpiresAt);

    if (shouldRefreshToken) {
      refreshingRef.current = true;
      prevTokenRef.current = aiChatToken;
      dispatch(getAiChatTokenRequest(forceTokenRefresh ? { force: true } : {}));
    }
  }, [active, aiChatToken, aiChatTokenExpiresAt, dispatch]);

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    const handleForegroundRefresh = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') {
        return;
      }

      refreshChatSession();
    };

    const handleBlur = () => {
      wasBackgroundedRef.current = true;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        wasBackgroundedRef.current = true;
        return;
      }

      if (document.visibilityState === 'visible') {
        if (!wasBackgroundedRef.current) {
          return;
        }

        wasBackgroundedRef.current = false;
        handleForegroundRefresh();
      }
    };

    const handleFocus = () => {
      if (!wasBackgroundedRef.current) {
        return;
      }

      wasBackgroundedRef.current = false;
      handleForegroundRefresh();
    };

    refreshChatSession();

    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handleForegroundRefresh);
    window.addEventListener('online', handleForegroundRefresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handleForegroundRefresh);
      window.removeEventListener('online', handleForegroundRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [active, refreshChatSession]);

  const handleAuthExpired = useCallback(() => {
    if (!refreshingRef.current) {
      refreshingRef.current = true;
      pendingAuthReconnectRef.current = true;
      prevTokenRef.current = aiChatToken;
      dispatch(getAiChatTokenRequest({ force: true }));
    }
  }, [aiChatToken, dispatch]);

  return { chatKey, handleAuthExpired, aiChatToken };
};

export default useAiChatSession;
